import React, { useState } from 'react';
import { getByNumero } from '../db';

// ── Configuración de estados ──────────────────────────────────
const STATUSES = [
  { key: 'Producción',            icon: '⏳', color: '#94a3b8', bg: '#f1f5f9' },
  { key: 'En puerto origen',      icon: '🏭', color: '#f59e0b', bg: '#fef3c7' },
  { key: 'En tránsito',           icon: '🚢', color: '#0057ff', bg: '#e8f0ff' },
  { key: 'En alta mar',           icon: '🌊', color: '#0ea5e9', bg: '#e0f2fe' },
  { key: 'Fondeo Puerto Quetzal', icon: '🏗️', color: '#8b5cf6', bg: '#ede9fe' },
  { key: 'En aduana',             icon: '🛃', color: '#f97316', bg: '#ffedd5' },
  { key: 'Selectivo',             icon: '✅', color: '#00b894', bg: '#e6faf6' },
  { key: 'En Bodega',             icon: '📦', color: '#00b894', bg: '#e6faf6' },
];

const STATUS_MAP = Object.fromEntries(STATUSES.map((s, i) => [s.key, { ...s, step: i }]));

function fmtDate(str) {
  if (!str) return '—';
  const d = new Date(str + 'T12:00:00');
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

function Card({ children, style }) {
  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--r)',
      boxShadow: 'var(--shadow)',
      ...style,
    }}>{children}</div>
  );
}

function InfoField({ icon, label, value, mono }) {
  return (
    <div style={{
      padding: '14px 18px',
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--r-sm)',
    }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 5, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 5 }}>
        <span>{icon}</span> {label}
      </div>
      <div style={{
        fontWeight: 500,
        fontSize: mono ? 12 : 14,
        fontFamily: mono ? 'var(--mono)' : 'var(--font)',
        color: value === '—' ? 'var(--text-muted)' : 'var(--text)',
        letterSpacing: mono ? 0.3 : 0,
      }}>{value || '—'}</div>
    </div>
  );
}

export default function TrackingPublico() {
  const [input, setInput] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const buscar = async (e) => {
    e?.preventDefault();
    const val = input.trim().toUpperCase();
    if (!val) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const data = await getByNumero(val);
      if (!data) {
        setError('No se encontró ninguna importación con ese número.');
      } else {
        setResult(data);
      }
    } catch (err) {
      setError('Error al conectar. Intenta nuevamente.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const cfg = result ? (STATUS_MAP[result.status] || STATUS_MAP['Pendiente']) : null;
  const currentStep = cfg?.step ?? -1;

  return (
    <main style={{ minHeight: 'calc(100vh - 60px)', padding: '0 0 60px' }}>

      {/* Hero banner */}
      <div style={{
        background: 'var(--navy)',
        padding: '52px 24px 64px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Decorative wave lines */}
        <svg style={{ position: 'absolute', bottom: 0, left: 0, right: 0, opacity: 0.08 }}
          viewBox="0 0 1440 80" preserveAspectRatio="none" height="80">
          <path d="M0,40 C180,70 360,10 540,40 C720,70 900,10 1080,40 C1260,70 1440,20 1440,40 L1440,80 L0,80 Z" fill="white"/>
        </svg>

        <div style={{ maxWidth: 580, margin: '0 auto', textAlign: 'center', position: 'relative' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            background: 'rgba(0,184,148,0.15)',
            border: '1px solid rgba(0,184,148,0.3)',
            borderRadius: 100, padding: '4px 14px',
            fontSize: 11, color: '#00b894',
            fontFamily: 'var(--mono)', letterSpacing: 1,
            marginBottom: 20,
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#00b894', display: 'inline-block', animation: 'pulse 2s infinite' }} />
            PORTAL DE TRACKING EN TIEMPO REAL
          </div>
          <style>{`
            @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
            @keyframes slideUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
          `}</style>

          <h1 style={{
            fontSize: 'clamp(28px, 5vw, 44px)',
            fontWeight: 800,
            color: '#fff',
            letterSpacing: '-1px',
            lineHeight: 1.1,
            marginBottom: 14,
          }}>
            Rastrea tu envío<br />
            <span style={{ color: '#00b894' }}>en tiempo real</span>
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 15, marginBottom: 36 }}>
            Ingresa tu número de importación para ver el estado actualizado de tu carga
          </p>

          {/* Search box */}
          <form onSubmit={buscar} style={{ display: 'flex', gap: 10, maxWidth: 480, margin: '0 auto' }}>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Ej: IMP-2024-001"
              style={{
                flex: 1, padding: '14px 18px',
                background: 'rgba(255,255,255,0.08)',
                border: '1.5px solid rgba(255,255,255,0.15)',
                borderRadius: 'var(--r-sm)',
                color: '#fff', fontSize: 15,
                fontFamily: 'var(--mono)', letterSpacing: 1,
                outline: 'none',
                transition: 'border-color 0.2s',
              }}
              onFocus={e => e.target.style.borderColor = '#00b894'}
              onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.15)'}
            />
            <button type="submit" disabled={loading} style={{
              padding: '14px 24px',
              background: loading ? '#005f4d' : '#00b894',
              color: '#fff',
              border: 'none', borderRadius: 'var(--r-sm)',
              fontWeight: 700, fontSize: 14,
              transition: 'background 0.2s',
              whiteSpace: 'nowrap',
            }}>
              {loading ? '...' : 'Buscar'}
            </button>
          </form>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{ maxWidth: 700, margin: '24px auto 0', padding: '0 24px' }}>
          <div style={{
            padding: '14px 18px',
            background: 'var(--red-lt)',
            border: '1px solid #fca5a5',
            borderRadius: 'var(--r-sm)',
            color: 'var(--red)',
            fontSize: 14,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <span style={{ fontSize: 18 }}>⚠️</span> {error}
          </div>
        </div>
      )}

      {/* Result */}
      {result && (
        <div style={{ maxWidth: 760, margin: '28px auto 0', padding: '0 24px', animation: 'slideUp 0.4s ease' }}>

          {/* Status header */}
          <Card style={{ padding: '24px 28px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--mono)', marginBottom: 6, letterSpacing: 0.5 }}>NÚMERO DE IMPORTACIÓN</div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 24, fontWeight: 500, color: 'var(--navy)', letterSpacing: 1 }}>
                {result.numeroImportacion}
              </div>
            </div>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              background: cfg.bg,
              border: `1.5px solid ${cfg.color}40`,
              borderRadius: 100, padding: '10px 20px',
            }}>
              <span style={{ fontSize: 22 }}>{cfg.icon}</span>
              <span style={{ fontWeight: 700, color: cfg.color, fontSize: 15 }}>{result.status}</span>
            </div>
          </Card>

          {/* Progress stepper */}
          <Card style={{ padding: '24px 28px', marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, marginBottom: 22, letterSpacing: 0.5 }}>PROGRESO DEL ENVÍO</div>

            {/* Desktop stepper */}
            <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
              <div style={{ position: 'relative', minWidth: 600 }}>
                {/* Background line */}
                <div style={{ position: 'absolute', top: 14, left: '3.5%', right: '3.5%', height: 2, background: 'var(--border)' }} />
                {/* Progress line */}
                <div style={{
                  position: 'absolute', top: 14, left: '3.5%', height: 2,
                  width: `${(currentStep / (STATUSES.length - 1)) * 93}%`,
                  background: `linear-gradient(90deg, #00b894, ${cfg.color})`,
                  transition: 'width 0.8s ease',
                }} />

                <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative' }}>
                  {STATUSES.map((s, i) => {
                    const done = i < currentStep;
                    const active = i === currentStep;
                    return (
                      <div key={s.key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, flex: 1 }}>
                        <div style={{
                          width: 28, height: 28, borderRadius: '50%', zIndex: 1,
                          background: done ? '#00b894' : active ? cfg.color : 'var(--surface)',
                          border: `2px solid ${done ? '#00b894' : active ? cfg.color : 'var(--border2)'}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: active ? 14 : 11,
                          boxShadow: active ? `0 0 0 4px ${cfg.color}25` : 'none',
                          transition: 'all 0.3s',
                        }}>
                          {done ? <span style={{ color: '#fff', fontWeight: 700, fontSize: 11 }}>✓</span>
                                : active ? s.icon
                                : <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--border2)', display: 'block' }} />}
                        </div>
                        <span style={{
                          fontSize: 10, fontWeight: active ? 700 : 400,
                          color: active ? cfg.color : done ? 'var(--text-mid)' : 'var(--text-muted)',
                          textAlign: 'center', lineHeight: 1.3, maxWidth: 56,
                        }}>{s.key}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </Card>

          {/* Info grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10, marginBottom: 10 }}>
            <InfoField icon="📤" label="Fecha ETD (Salida)" value={fmtDate(result.fechaEtd)} />
            <InfoField icon="📥" label="Fecha ETA (Llegada)" value={fmtDate(result.fechaEta)} />
            <InfoField icon="📄" label="# BL" value={result.bl} mono />
            <InfoField icon="📦" label="# Contenedor" value={result.contenedor} mono />
            <InfoField icon="🚢" label="Nombre del Barco" value={result.nombreBarco} />
            <InfoField icon="🏢" label="Naviera" value={result.naviera} />
          </div>

          {/* Posición */}
          {result.posicion && (
            <div style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-sm)',
              padding: '14px 18px',
              marginBottom: 10,
              display: 'flex', alignItems: 'flex-start', gap: 12,
            }}>
              <span style={{ fontSize: 20 }}>📍</span>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, marginBottom: 4 }}>Comentario:</div>
                <div style={{ fontSize: 14 }}>{result.posicion}</div>
              </div>
            </div>
          )}

          {/* Footer timestamp */}
          <div style={{ textAlign: 'right', padding: '4px 4px 0', fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--mono)' }}>
            Última actualización:{' '}
            {result.updatedAt?.toDate
              ? result.updatedAt.toDate().toLocaleString('es-ES')
              : '—'}
          </div>
        </div>
      )}
    </main>
  );
}
